<template>
  <div id="cash-log-editor" :class="{ active: isActive }">
    <div
      class="header"
      :class="{ in: (direction > 0), out: (direction < 0) }"
    >
      <h3>
        <span>{{ $t((direction > 0) ? 'CASH_IN' : 'CASH_OUT') }}</span>
        <span v-if="!!item">{{ item.id ? ` (#${item.id})` : '' }}</span>
      </h3>
      <div class="close" @click="() => hide()">
        <div class="line line-1"></div>
        <div class="line line-2"></div>
      </div>
    </div>

    <div class="body">
      <bamboo-input
        v-if="isHeadAccountant"
        :items="funds"
        :label="$t('FUND')"
        v-model="fund"
        ref="fundInput"
      />

      <toggle-box ref="registrationDateBox">
        <template v-slot:control>
          <text-input
            v-model="registrationDate"
            @focus="() => showCalendar(true)"
            ref="registrationDateInput"
            :label="$t('DATE')"
            dynamic-label
            required
            class="registration-date-input"
          >
            <template v-slot:icon>
              <font-awesome-icon class="icon" :icon="faCalendarAlt" />
            </template>
          </text-input>
        </template>
        <template v-slot:content>
          <calendar
            :date="registrationDate || undefined"
            @dateChange="setRegistrationDate"
          />
        </template>
      </toggle-box>

      <text-input
        v-model="amount"
        ref="amountInput"
        :label="$t('AMOUNT')"
        dynamic-label
        required
        :format="addCommas"
        :hint="changeDigitToViText(amount, false)"
        class="amount-input"
      />

      <auto-complete-input
        :items="(direction > 0) ? cashInCategories : cashOutCategories"
        :label="$t('CASH_LOG_CATEGORY')"
        v-model="category"
        required
        dynamic-label
        ref="categoryInput"
      />

      <auto-complete-input
        v-if="subCategories.length > 0"
        :items="subCategories"
        :label="$t('CASH_LOG_DETAIL_CATEGORY')"
        v-model="subCategory"
        required
        dynamic-label
        ref="subCategoryInput"
      />

      <bamboo-input
        v-if="isHeadAccountant"
        :items="types"
        :label="$t('CASH_LOG_TYPE')"
        :value="type"
        required
        ref="typeInput"
      />
      <bamboo-input
        v-else
        :items="types"
        :label="$t('CASH_LOG_TYPE')"
        v-model="type"
        required
        ref="typeInput"
      />

      <bamboo-input
        :items="branches"
        :label="$t('BRANCH')"
        v-model="branch"
        required
        ref="branchInput"
      />

      <text-input
        v-if="mifosLoanIdShow"
        type="positiveNumber"
        v-model="mifosLoanId"
        ref="mifosLoanIdInput"
        :label="$t('CNTRCT_ID')"
        dynamic-label
        class="contract-id-input"
        required
      />

      <text-input
        v-if="mifosRenewLoanIdShow"
        type="positiveNumber"
        v-model="mifosRenewLoanId"
        ref="mifosRenewLoanIdInput"
        :label="$t('RENEW_CNTRCT_ID')"
        dynamic-label
        class="contract-id-input"
        required
      />

      <text-input
        v-model="notes"
        ref="notesInput"
        :label="$t('NOTES')"
        dynamic-label
      />

      <primary-button rounded-corner :text="$t('OK')" blue @click="() => submit()" />
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted, watch } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

import { DATE_FORMAT, CASH_LOG_TYPES, PERMISSIONS, ERROR_CODES } from '@/consts';
import { addCommas, removeCommas, changeDigitToViText } from '@/helpers';
import { hasPermissions } from '@/helpers/permission';

import TextInput from '@/components/TextInput';
import Calendar from '@/components/Calendar';
import ToggleBox from '@/components/ToggleBox';
import BambooInput from '@/components/BambooInput';
import AutoCompleteInput from '@/components/AutoCompleteInput';
import PrimaryButton from '@/components/PrimaryButton';

import cashLogService from '@/views/CashLog/CashLogService';


const { useState, useActions } = createNamespacedHelpers('cashLog');
const { useState: useAppState, useActions: useAppActions } = createNamespacedHelpers('app');
const { useState: useUserState } = createNamespacedHelpers('user');
const { useActions: useFlashActions } = createNamespacedHelpers('flash');

export default {
  components: {
    TextInput,
    Calendar,
    ToggleBox,
    FontAwesomeIcon,
    BambooInput,
    AutoCompleteInput,
    PrimaryButton
  },
  props: {
    dir: {
      type: Number,
      default: 1
    },
    active: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit, root }) {
    const { cashLogCategories: categories, branches: branches_ } = useAppState(['cashLogCategories', 'branches']);
    const { branch: userBranchName } = useUserState(['branch']);
    const { error } = useFlashActions(['error']);
    const { funds: funds_ } = useAppState(['funds']);

    const userBranch = computed(() => branches_.value.find((item) => item.name === userBranchName.value));

    const { getCashLogCategories } = useAppActions(['getCashLogCategories']);
    const cashInCategories = computed(() => categories.value.filter((c) => (c.direction > 0 && c.parent === null)).map((c) => ({
      value: c.id,
      text: root.$t(c.key)
    })));
    const cashOutCategories = computed(() => categories.value.filter((c) => (c.direction < 0 && c.parent === null)).map((c) => ({
      value: c.id,
      text: root.$t(c.key)
    })));

    const branches = computed(() => branches_.value.filter((b) => b.name !== 'LVL' && b.name !== 'GD').map((b) => ({
      value: b.id,
      text: b.name
    })));

    const types = Object.keys(CASH_LOG_TYPES).map((key) => ({
      value: key,
      text: root.$t(key)
    }));

    const { targetingItem: item } = useState(['targetingItem']);
    const { setTargetingItem } = useActions(['setTargetingItem']);
    const isActive = computed(() => props.active || !!item.value);

    const direction = computed(() => (item.value?.direction || props.dir));
    const registrationDate = ref(moment(item.value?.registrationDate || undefined).format(DATE_FORMAT));
    const registrationDateBox = ref(null);
    const registrationDateInput = ref(null);

    const amount = ref(item.value?.amount || 1000);
    const amountInput = ref(null);

    const category = ref(item.value?.category || null);

    const subCategories = computed(() => {
      if (category.value !== null) {
        return categories.value.filter(
          (c) => (c.parent === category.value)
        ).map((c) => ({
          value: c.id,
          text: root.$t(c.key)
        }));
      }
      return [];
    });

    const subCategory = ref(null);
    const subCategoryInput = ref(null);

    const branch = ref(item.value?.branch || userBranch.value?.id || null);
    const type = ref(item.value?.type || CASH_LOG_TYPES.CASH);

    const mifosLoanId = ref(item.value?.mifosLoanId || '');
    const mifosLoanIdInput = ref(null);
    const mifosLoanIdShow = computed(() => {
      const categoryChoose = categories.value.find((c) => c.id === category.value);
      if (categoryChoose) {
        if (['LOAN_REPAYMENT', 'LOAN_DISBURSEMENT', 'PROCEEDS_FROM_SELLING_COLLATORAL'].includes(categoryChoose.key)) {
          return true;
        }
      }
      return false;
    });

    const mifosRenewLoanId = ref(item.value?.mifosRenewLoanId || '');
    const mifosRenewLoanIdInput = ref(null);
    const mifosRenewLoanIdShow = computed(() => {
      const subCategoryChoose = categories.value.find((c) => c.id === subCategory.value);

      if (subCategoryChoose) {
        if (subCategoryChoose.key === 'RENEW') {
          return true;
        }
      }
      return false;
    });

    const notes = ref(item.value?.notes || '');

    const categoryInput = ref(null);
    const typeInput = ref(null);
    const branchInput = ref(null);

    const funds = computed(() => funds_.value.map((b) => ({
      value: b.id,
      text: b.name
    })));

    const fund = ref(null);
    const fundInput = ref(null);

    const isHeadAccountant = computed(() => hasPermissions([PERMISSIONS.HEAD_ACCOUNTANT_ACCESS]));

    const hide = () => {
      setTargetingItem({ item: null });
      emit('close');
    };

    const setRegistrationDate = (val) => {
      registrationDate.value = val;
      registrationDateBox.value.toggle(false);
    };

    const showCalendar = () => {
      registrationDateBox.value.toggle(true);
      registrationDateInput.value.blur();
    };

    const submit = async () => {
      const inputs = [
        registrationDateInput.value,
        amountInput.value,
        categoryInput.value,
        typeInput.value,
        branchInput.value
      ];

      if (fundInput.value) inputs.push(fundInput.value);

      if (subCategoryInput.value) inputs.push(subCategoryInput.value);

      if (mifosLoanIdInput.value) inputs.push(mifosLoanIdInput.value);

      if (mifosRenewLoanIdInput.value) inputs.push(mifosRenewLoanIdInput.value);

      let valid = true;

      inputs.forEach((input) => {
        const inputValidity = input.validateInput();
        valid = valid && inputValidity;
      });

      if (valid) {
        const data = {
          direction: direction.value,
          registrationDate: registrationDate.value,
          amount: removeCommas(amount.value),
          category: subCategory.value || category.value,
          type: type.value,
          targetBranch: branch.value,
          mifosLoanId: mifosLoanId.value || undefined,
          mifosRenewLoanId: mifosRenewLoanId.value || undefined,
          notes: notes.value || undefined,
          retractedLog: item.value?.id || undefined
        };

        if (isHeadAccountant.value) data.fund = fund.value || undefined;

        try {
          await cashLogService.createCashLog(data);
          setTargetingItem({ item: null });
          emit('submitted');
        } catch (err) {
          const { code, message } = err;
          const cashLogErrorCodes = [
            ERROR_CODES.CASH_LOG_CANNOT_BE_BEFORE_CLOSURE_DATE,
            ERROR_CODES.CASH_LOG_FUND_NOT_FOUND,
            ERROR_CODES.CASH_LOG_BRANCH_NOT_FOUND,
            ERROR_CODES.CASH_LOG_CANNOT_BE_A_FUTURE_DATE,
          ];
          const i = cashLogErrorCodes.findIndex((c) => c === code);

          if (i === 0) {
            error('CASH_LOG_CANNOT_BE_BEFORE_CLOSURE_DATE');
          } else if (i === 1) {
            error('CASH_LOG_FUND_NOT_FOUND');
          } else if (i === 2) {
            error('CASH_LOG_BRANCH_NOT_FOUND');
          } else if(i === 3) {
            error('CASH_LOG_CANNOT_BE_A_FUTURE_DATE');
          } else {
            error(message);
          }
        }
      }
    };

    watch(isActive, (val) => {
      if (val) {
        registrationDate.value = moment(item.value?.registrationDate || undefined).format(DATE_FORMAT);
        amount.value = (item.value?.amount || 1000);
        type.value = (item.value?.type || CASH_LOG_TYPES.CASH);

        if (item.value) {
          category.value = (item.value.category.parent ? item.value.category.parent : item.value.category.id);
          // Wait subCategories loaded
          setTimeout(() => {
            subCategory.value = (item.value.category.parent ? item.value.category.id : null);
          });
        } else {
          category.value = null;
          subCategory.value = null;
        }

        branch.value = (item.value?.targetBranch.id || userBranch.value?.id || null);
        mifosLoanId.value = (item.value?.mifosLoanId || '');
        mifosRenewLoanId.value = (item.value?.mifosRenewLoanId || '');
        notes.value = (item.value?.notes || '');
      }
    });

    watch(subCategories, () => { subCategory.value = null; });

    watch(fund, () => {
      if (isHeadAccountant.value) {
        type.value = funds_.value.find((f) => f.id === fund.value)?.type;
      }
    });

    onMounted(() => {
      if (!categories.value.length) {
        getCashLogCategories();
      }
    });

    return {
      isActive,
      direction,
      hide,
      item,
      faCalendarAlt,
      registrationDate,
      registrationDateBox,
      registrationDateInput,
      setRegistrationDate,
      showCalendar,
      amount,
      amountInput,
      addCommas,
      changeDigitToViText,
      cashInCategories,
      cashOutCategories,
      subCategories,
      category,
      subCategory,
      subCategoryInput,
      branches,
      branch,
      types,
      type,
      mifosLoanId,
      mifosLoanIdInput,
      mifosLoanIdShow,
      mifosRenewLoanId,
      mifosRenewLoanIdInput,
      mifosRenewLoanIdShow,
      notes,
      submit,
      categoryInput,
      typeInput,
      branchInput,
      funds,
      fund,
      fundInput,
      isHeadAccountant,
    };
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";

#cash-log-editor {
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 400px;
  border-left: 1px solid $alto;
  background-color: $babyPowder;
  padding: 15px;
  z-index: 999;
  box-sizing: border-box;
  transform: translateX(100%);
  transition: all .2s ease-in-out;
  overflow-y: auto;

  &.active {
    transform: none;
  }

  .header {
    background-color: $geranium;
    padding: 10px 15px;
    padding-bottom: 7px;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;

    &.in {
      background-color: $shamrockGreen;
    }

    h3 {
      font-size: 18px;
      color: $babyPowder;
    }

    .close {
      margin-left: 15px;
      display: inline-block;
      position: absolute;
      top: 10px;
      right: 15px;
      width: 20px;
      height: 20px;
      cursor: pointer;
      opacity: .5;
      transition: all .2s ease-in-out;

      .line {
          height: 1px;
          width: 20px;
          background-color: $babyPowder;
          transform-origin: center;
          margin: 0 auto;
      }

      .line-1 {
          transform: translateY(11px) rotate(45deg);
      }

      .line-2 {
          transform: translateY(10px) rotate(135deg);
      }

      &:hover {
        opacity: 1;
      }
    }
  }

  .body {
    margin-top: 50px;

    > .input, > .toggle-box {
      margin-bottom: 20px;
      width: 100%;

      > input,
      > .display,
      > .input > input,
      .text-input > input {
        border-radius: 7px;
      }
    }

    .registration-date-input {
      width: 140px;
    }

    .amount-input,
    .contract-id-input {
      input {
        width: 50%;
        display: block;
      }
    }

    .primary-button {
      width: 100%;
    }
  }

  @media (max-width: 767px) {
    width: auto;
  }
}
</style>
